// The two ends of a trip, whatever they are: a railway station, or a stop of a
// mode with stops of its own (a tram stop today, a bus stop behind a flag).
//
// ONE PLANNER MEANS ONE VOCABULARY. A board of any stop can be given a
// destination that is any station or stop, so the destination chip, the sheet
// that picks it and the planner that answers all need one shape for "a place".
// It is the shape the mode planner has always sent (src/mode-plan.js):
//
//   { kind: 'station', stopId, name }
//   { kind: '<mode id>', stopIds: [..sides of the stop], stopId, name }
//
// plus the small memory a destination needs: the board it was set on, for as
// long as the session lasts (a plan is a statement about the next hour, and a
// destination nobody chose is the app asking the same question every morning,
// so nothing here is written to storage).

import { ref } from 'vue';
import { everyAdapterMode } from './modes.js';
import { modeOf as pickedModeOf } from './stop-search.js';
import { stationById } from './network.js';

/** The adapter mode a sheet row is a stop of, or null for a station. */
export function modeOfPickedRow(row) {
  const id = pickedModeOf(row);
  return id === 'train' ? null : everyAdapterMode().find((m) => m.id === id) ?? null;
}

/** A station, as an end. */
export const stationEnd = (station) => ({
  kind: 'station',
  stopId: Number(station?.stopId),
  name: String(station?.name ?? '').trim(),
});

/** A mode's stop (all its sides), as an end. */
export const stopEnd = (mode, stopIds, name) => {
  const ids = [...new Set((stopIds ?? []).map(Number).filter((n) => n > 0))];
  return { kind: mode.id, stopIds: ids, stopId: ids[0], name: String(name ?? '').trim() };
};

/** What identifies an end: the same place is the same key, whichever side of the road it came from. */
export function endKey(end) {
  if (!end) return '';
  if (end.kind === 'station') return `station:${Number(end.stopId)}`;
  const ids = [...(end.stopIds ?? [end.stopId])].map(Number).sort((a, b) => a - b);
  return `${end.kind}:${ids.join(',')}`;
}

/** Do these two ends name the same place? */
export const sameEnd = (a, b) => Boolean(a) && Boolean(b) && endKey(a) === endKey(b);

/** Does this end include a stop with this id (of its kind)? */
export const endHas = (end, kind, stopId) => Boolean(end) && end.kind === kind
  && (end.kind === 'station' ? Number(end.stopId) === Number(stopId)
    : (end.stopIds ?? [end.stopId]).map(Number).includes(Number(stopId)));

/** The end as the trip screens carry it (`trip.from` / `trip.to`): a station is a plain place. */
export function tripPlace(end) {
  if (!end) return null;
  const base = { stopId: Number(end.stopId), name: end.name ?? '', suburb: '' };
  return end.kind === 'station' ? base : { ...base, kind: end.kind, stopIds: end.stopIds };
}

// ── Where each board's destination is kept ───────────────────────────────────
//
// Keyed by the board's own end, in memory. `held` is the swapped trip: the
// board that is about to open should start with the far end already chosen.

const kept = new Map();
const tick = ref(0);

/** The destination set on this board in this session, or null. Reactive. */
export function keptDestination(origin) {
  void tick.value;
  return kept.get(endKey(origin)) ?? null;
}

/** Set (or, with null, clear) the destination of a board. */
export function keepDestination(origin, dest) {
  const key = endKey(origin);
  if (!key) return;
  if (dest) kept.set(key, dest); else kept.delete(key);
  tick.value += 1;
}

/** Test seam. */
export function forgetDestinations() {
  kept.clear();
  tick.value += 1;
}

/**
 * An end with its name filled in. A link or an old URL names a place by id
 * alone; the name is read off the station table, or off the mode's stop table
 * once it has loaded. `infoOf(mode, stopId)` is that table's answer (`{ display }`)
 * or null until it has loaded, and the end is then returned as it was so the
 * caller can try again. Handed in, so this module (which the first screen
 * imports) never imports the stop tables.
 */
export function named(end, infoOf = () => null) {
  if (!end || end.name) return end;
  if (end.kind === 'station') {
    const name = stationById(end.stopId)?.name;
    return name ? { ...end, name } : end;
  }
  const mode = everyAdapterMode().find((m) => m.id === end.kind);
  const info = mode ? infoOf(mode, end.stopId) : null;
  return info ? { ...end, name: info.display } : end;
}

// ── Asking the board to open its destination sheet ───────────────────────────
//
// An old link to the retired Plan screen names where the trip starts and not
// where it ends: the answer is the board of the start with the sheet that
// picks the destination already open. Left here and taken once by whichever
// board is entered, the way a requested board is (src/board-request.js).

const sheetAsked = ref(false);

export function askDestinationSheet() { sheetAsked.value = true; }

/** True once, if a screen asked for the sheet. */
export function takeDestinationSheet() {
  const asked = sheetAsked.value;
  sheetAsked.value = false;
  return asked;
}
